<script setup>
import moment from 'moment';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { reactive, ref, computed, onMounted, defineAsyncComponent, defineEmits, defineProps } from 'vue';
import SnackbarComposables from '../../composables/SnackbarComposables';
import ZdhcService from '../../services/Zdhc/ZdhcService';
import { freezesItsPeriod } from '../../utils/inventoryPeriod';
import {
    MATCH_STATUS,
    displayedReferenceId,
    matchStatusClasses,
    matchStatusLabel,
    needsResolution,
    resolveMatchStatus
} from '../../utils/matchStatus';
/* Components */
const MatchDetailsModal = defineAsyncComponent( () => import ('../../components/modals/MatchModal'));
const NoMatchModal = defineAsyncComponent( () => import ('../../components/modals/NoMatchModal'));
const ProductStatusModal = defineAsyncComponent( () => import ('../../components/modals/ProductStatusModal'));
//const MatchesModal = defineAsyncComponent( () => import ('../../components/modals/MatchesModal'));
const UpdateQuantityModal = defineAsyncComponent( () => import ('../../components/modals/UpdateQuantityModal.vue'));
/* Emits */
const emits = defineEmits(['productDeleted', 'selectProduct', 'productReferenceIdUpdated']);
/* Props */
const props = defineProps({
    parent_product: {required:true},
    isSelected: {required: false, type: Boolean, default: false},
    selectedDate: {required: true},
    // Il periodo selezionato ha gia' prodotto un report: nessuno stato si tocca.
    // Lo sa la tabella, che lo riceve col periodo.
    periodReported: {required: false, type: Boolean, default: false},
});
/* Data */
const {t} = useI18n();
//const matches = ref([]);
const store = useStore();
const product = reactive({});
const splittedName = ref([]);
const deleteModal = ref(false);
//const matchesModal = ref(false);
const showMatchModal = ref(false);
const showNoMatchModal = ref(false);
const showProductStatusModal = ref(false);
const showUpdateQuantityModal = ref(false);
const { addSnackbar } = SnackbarComposables();
const { resolveMatch } = ZdhcService();
// Vero mentre il backend sta valutando questa riga: e' l'unica cosa che
// l'utente deve capire mentre aspetta — qualcosa sta succedendo, e riguarda
// questo prodotto.
const resolving = ref(false);
// Quanto si aspetta prima di richiedere una riga che qualcun altro stava
// lavorando: il tempo tipico di una risoluzione, non quello massimo — se non e'
// pronta si mostra cio' che c'e' invece di girare a vuoto.
const RECLAIM_DELAY_MS = 4000;
//const suggestions_matches = ref([]);
// const { searchMatchById, searchMatchByWord } = ZdhcProductService();
//const { searchSuggestionById, searchSuggestionByName } = SuggestionService();
/* Computed */
/**
 * Lo stato non si calcola piu' qui: lo decide il risolutore lato backend e la
 * riga lo legge. Essendo derivato dal periodo, si aggiorna da solo quando una
 * azione dell'utente ne cambia le colonne — prima andava riassegnato a mano a
 * ogni transizione, ed era li' che si perdeva.
 */
const status = computed( () => resolveMatchStatus(product.incheck_reference));
// La dashboard mostra l'identita' salvata sul prodotto, inclusa quella
// dichiarata dal CSV prima che esista un'associazione per il periodo; dove
// quella manca, l'associazione del periodo riempie la cella invece di lasciarla
// vuota — e' il caso dei match scelti a mano.
const referenceId = computed( () => displayedReferenceId(product, product.incheck_reference));
const setStatusClasses = computed( () => matchStatusClasses(status.value));
const setStatusContent = computed( () => matchStatusLabel(status.value));
const setCreatedAtDate = computed( () => 
{
    return product.created_at ? moment(product.created_at).format('YYYY-MM-DD') : '-';
});
const setFormulatorName = computed ( () => 
{
    return product.formulator 
            ? (product.formulator.name.length > 15 
                ? product.formulator.name.substring(0, 12)+'...' 
                : product.formulator.name) 
            : product.formulator_name 
                ? (product.formulator_name.length > 15
                    ? product.formulator_name.substring(0, 12)+'...'
                    :product.formulator_name)
                : '-';
});
const formulatorIsMatched = computed ( () => 
{
    return product.formulator 
            ? true 
            : false;
});
/* Methods */
//const closeMatchesModal = () => matchesModal.value = false;
const closeDeleteProductModal = () => 
{
    deleteModal.value = false
};
const closeShowMatchModal = () => 
{
    showMatchModal.value = false;
};
/* const updateProductSuggestion = (suggestion) => 
{
    product.incheck_reference.suggestion = suggestion;
    product.incheck_reference.suggestion_id = suggestion.id; 
    product.incheck_reference.no_match_id = null; 
    if(!suggestions_matches.value.some(s => s.zdhc_product_id === suggestion.zdhc_product_id))
    {
        let jsonName = JSON.parse(suggestion.zdhc_product.name)
        suggestions_matches.value.push(
            {
                name: jsonName[store.state.locale] ? jsonName[store.state.locale].toLowerCase() 
                : (jsonName['en'] ? jsonName['en'].toLowerCase() : jsonName[Object.keys(jsonName)[0]].toLowerCase()),
                id: suggestion.zdhc_product.id,
                guid: suggestion.zdhc_product.guid,
                count: suggestion.count
            }
        )
    }
    matchesModal.value = false;
    status.value = 'suggestion';
}; */
// Gateway 2.0: no match is a local-only boolean flag — no placeholder
// product (and no guid) is created on the gateway anymore.
const updateProductNoMatch = () =>
{
    product.incheck_reference.no_match = true;
    product.incheck_reference.no_match_id = null;
    product.incheck_reference.no_match_at = props.selectedDate;
    product.incheck_reference.previous_results_at = props.selectedDate;
    product.incheck_reference.suggestion_id = null;
    product.incheck_reference.suggestion = null;
    product.incheck_reference.zdhc_product_id = null;
    product.incheck_reference.match = null;
    // Lo stato persistito e' quello che la riga mostra: aggiornare solo le
    // colonne dell'associazione lascerebbe l'etichetta a dire quello di prima.
    product.incheck_reference.match_status = MATCH_STATUS.NO_MATCH;
    showProductStatusModal.value = false;
};
/**
 * L'utente ha scelto fra i candidati: la riga prende quello che il backend ha
 * scritto, invece di dedurre lo stato dalla scelta. Se sia diventato un match
 * esatto o un manual match lo decide lui, e qui si legge.
 */
const applyMatchResult = (result) =>
{
    if(!result?.incheck_reference) return false;

    Object.assign(product.incheck_reference, result.incheck_reference);

    // Campo autorevole del prodotto, non l'id dell'associazione: sui manual
    // match i due possono differire. La guardia mantiene la compatibilita' con
    // un'API precedente durante un deploy progressivo.
    if(Object.prototype.hasOwnProperty.call(result, 'product_reference_id'))
    {
        product.product_reference_id = result.product_reference_id;
        // `product` e' una copia reattiva superficiale della prop: aggiornare
        // solo questa riga lascerebbe invariato `products.data`, che e' la
        // sorgente dell'export. Il parent mantiene allineata la collezione.
        emits('productReferenceIdUpdated', {
            productId: product.id,
            productReferenceId: result.product_reference_id,
        });
    }

    return true;
};
const associationChosen = (result) =>
{
    applyMatchResult(result);
    showProductStatusModal.value = false;
};
/**
 * Quello che il modale mostrava non e' piu' vero — un'altra scheda ha scelto, o
 * la lista dei candidati e' invecchiata. Riaprire non basterebbe: la ricostruire
 * dagli stessi dati locali. Rileggere lo stato dal backend e' l'unica cosa che
 * la aggiorna davvero.
 */
const refreshFromBackend = async () =>
{
    showProductStatusModal.value = false;

    await resolveOwnStatus();
};
const productDeleted = (id) => 
{
    emits('productDeleted', id)
};
/**
 * Questo prodotto e' in un report che tiene chiuso il suo periodo.
 *
 * Che un inventario esista non basta. Una bozza e' ancora ritentabile, e un
 * report **rifiutato** si corregge cambiando le associazioni e reinviando: e'
 * l'unico rimedio previsto dall'api SP ZDHC, e con il cestino bloccato un
 * prodotto entrato per errore nel mese non si poteva piu' togliere. Bloccano
 * `submitted`, `published` e i legacy a `status` null, che valgono come pdf
 * gia' pubblicati. Il predicato per stato e' quello di `utils/inventoryPeriod`,
 * lo stesso della modale del nuovo report, per non averne una seconda
 * definizione che divergerebbe.
 *
 * Resta **per prodotto e periodo**, non per periodo come la matita qui
 * accanto: un prodotto creato dopo l'invio non e' in quel report — un secondo
 * inventario per il mese l'api lo rifiuta, quindi non ci entrera' mai — e
 * cancellarlo non cambia niente di cio' che e' stato dichiarato.
 */
const checkForInventory = () => 
{
    let hasInventory = false;
    let incheck_date = moment(product.incheck_reference.reference_date, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD');
    if(product.inventories.length)
    {
        hasInventory = product.inventories.some( i => 
        {
            if(! freezesItsPeriod(i)) return false;

            let inventory_date = moment(i.reference_date, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD');
            return moment(inventory_date).isSame(moment(incheck_date));
        });
    }
    return hasInventory;
};
const handleButtonClick = () => 
{
    return showProductStatusModal.value = true;
};
const openDeleteModal = () => 
{
    let hasInventory = checkForInventory();
    // If product is in inventory with acutal reference_date, cannot be destroyed
    if(hasInventory)
    {
        addSnackbar('warning', t('product.delete.hasInventory', store.state.locale))
    }
    // Non c'e' piu' un'attesa da presidiare: lo stato arriva gia' risolto col
    // prodotto, quindi la riga non attraversa piu' una fase in cui cancellare
    // avrebbe interrotto un controllo a meta'.
    else deleteModal.value = true;
};
const selectProduct = () => 
{
    let hasInventory = checkForInventory();
    if(!hasInventory) emits('selectProduct', product);
};
const updateProductQuantity = (value) => 
{
    product.incheck_reference.quantity = value * 100;
    closeUpdateQuantityModal();
};
/**
 * Il periodo ha prodotto un report: la quantita' e' il peso dichiarato a ZDHC
 * e l'api risponde 409. Il modale non si apre e l'utente lo scopre subito, non
 * dopo aver scritto un numero. Stesso trattamento del cestino qui accanto, che
 * su un prodotto in inventario avvisa e non apre.
 */
const openUpdateQuantityModal = () =>
{
    if(props.periodReported)
    {
        addSnackbar('warning', t('updateQuantityModal.periodReported', store.state.locale));

        return;
    }

    showUpdateQuantityModal.value = true;
};
const closeUpdateQuantityModal = () => showUpdateQuantityModal.value = false;
/**
 * Chiede al backend di valutare lo stato di questa riga.
 *
 * A interrogare ZDHC non e' piu' il browser: qui si chiede e si aspetta. La
 * riga non conclude niente da sola — se la richiesta fallisce resta lo stato
 * che c'era, che e' sempre meglio di uno inventato.
 */
const resolveOwnStatus = async (retriesLeft = 1) =>
{
    // Il periodo ha prodotto un report: lo stato che si vede e' quello che il
    // report ha raccontato, e il backend rifiuterebbe di rivalutarlo. Chiederlo
    // comunque vorrebbe dire una richiesta per riga per riavere lo stesso stato.
    if(props.periodReported) return;

    if(!needsResolution(product.incheck_reference)) return;

    resolving.value = true;

    try
    {
        const res = await resolveMatch(product.incheck_reference.id);

        if(res && !res.error && applyMatchResult(res))
        {
            // Se ne sta occupando qualcun altro — un'altra scheda, o questa
            // riga rimontata mentre la richiesta precedente lavorava ancora.
            // Quello che torna e' la fotografia di prima: smettere di girare
            // qui vorrebbe dire mostrare lo stato vecchio come se fosse
            // l'esito. Si riprova una volta, e se ancora non e' pronto si
            // mostra cio' che c'e'.
            if(res.outcome === 'claimed_elsewhere' && retriesLeft > 0)
            {
                await new Promise(resolve => setTimeout(resolve, RECLAIM_DELAY_MS));

                return resolveOwnStatus(retriesLeft - 1);
            }
        }
    }
    catch(e)
    {
        // Un guasto di rete non e' un esito: la riga torna a mostrare quello
        // che sapeva.
    }
    finally
    {
        resolving.value = false;
    }
};
/* Hooks */
moment.locale(store.state.locale);
onMounted( async () =>
{
    Object.assign(product, props.parent_product);
    product.incheck_reference = product.incheck_references[0];
    splittedName.value = props.parent_product.name.split(' ');

    resolveOwnStatus();
});
</script>
<template>
    <tr v-if="Object.keys(product).length" class="bg-white dark:bg-gray-900 dark:hover:bg-gray-700 duration-300"> 
        <td class="px-4 py-3">
            <button 
                class="w-5 h-5 rounded border text-white flex items-center justify-center"
                :class="isSelected ? 'bg-light-green-500 border-light-green-500' : 'bg-gray-100 dark:bg-gray-700 dark:border-gray-500'"
                @click.prevent="selectProduct">
                <svg v-show="isSelected" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 fill-current"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>    
            </button>
        </td>
        <!-- Product Id -->
        <td class="px-4 py-3">
            <span class="text-sm font-thin">
                {{product.id}}
            </span>
        </td>
        <!-- Name -->
        <td class="px-4 py-3">
            <span v-html="product.name"></span>
        </td>
        <!-- Reference Id -->
        <td class="px-4 py-3">
            <span class="text-sm font-thin">{{ referenceId ?? '-' }}</span>
        </td>
        <!-- Quantity -->
        <td class="px-4 py-3">
            <span class="text-sm font-thin">
                {{ product.incheck_reference.quantity /100 }}
            </span>
        </td>
        <!-- Formulator -->
        <td class="px-4 py-3 flex items-center justify-start space-x-1" :class="formulatorIsMatched ? 'text-light-green-500' : ''">
            <svg v-if="formulatorIsMatched" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 min-w-4 fill-current"><path d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,.71.29,1,1,0,0,0,.7-.29l5-5a1,1,0,0,0,0-1.42A1,1,0,0,0,14.72,8.79ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z"/></svg>
            <span 
                class="text-sm font-thin">
                {{ setFormulatorName }}
            </span>
        </td>
        <!-- Status -->
        <!-- `aria-live` sulla cella e non sul pulsante: l'esito arriva quando
             l'utente sta gia' guardando altrove, e senza annuncio chi usa uno
             screen reader non saprebbe mai che la verifica e' finita. -->
        <td class="px-4 py-3" aria-live="polite">
            <!-- Mentre il backend valuta, la riga gira: l'utente capisce che
                 qualcosa sta succedendo, e che riguarda questo prodotto. Il
                 badge non apre niente finche' non c'e' un esito da rivedere. -->
            <button
                class="py-1 px-2 text-xs rounded-full flex items-center justify-center whitespace-nowrap"
                :class="[resolving ? 'bg-blue-500 text-white cursor-default' : setStatusClasses]"
                :disabled="resolving"
                :aria-busy="resolving"
                :aria-label="resolving ? $t('productTable.resolving', $store.state.locale) : setStatusContent"
                @click.prevent="handleButtonClick">
                <svg v-if="resolving" class="animate-spin w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                <template v-else>{{ setStatusContent }}</template>
            </button>
        </td>

        <!-- Created At -->
        <td class="px-4 py-3">
            <span class="text-sm font-thin">
                {{ setCreatedAtDate }}
            </span>
        </td>
        <!-- Actions -->
        <td class="px-4 py-3">
            <div class="flex items-center justify-center">
                <button 
                    class="max-w-min rounded p-1 flex items-center justify-center text-white text-green-500 hover:text-white hover:bg-green-500 duration-300"
                    :title="$t('updateQuantityModal.title', $store.state.locale)"
                    @click.prevent="openUpdateQuantityModal">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current"><path d="M21,12a1,1,0,0,0-1,1v6a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V5A1,1,0,0,1,5,4h6a1,1,0,0,0,0-2H5A3,3,0,0,0,2,5V19a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V13A1,1,0,0,0,21,12ZM6,12.76V17a1,1,0,0,0,1,1h4.24a1,1,0,0,0,.71-.29l6.92-6.93h0L21.71,8a1,1,0,0,0,0-1.42L17.47,2.29a1,1,0,0,0-1.42,0L13.23,5.12h0L6.29,12.05A1,1,0,0,0,6,12.76ZM16.76,4.41l2.83,2.83L18.17,8.66,15.34,5.83ZM8,13.17l5.93-5.93,2.83,2.83L10.83,16H8Z"/></svg>
                </button>
                <button 
                    @click.prevent="openDeleteModal"
                    class="max-w-min rounded p-1 flex items-center justify-center text-white text-red-500 hover:text-white hover:bg-red-500 duration-300"
                    :title="$t('actions.delete', $store.state.locale)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current"><path d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"/></svg>
                </button>
            </div>
        </td>
    </tr>
    <teleport to="body">
        <ProductStatusModal 
            v-if="showProductStatusModal"
            :product="product"
            :periodReported="props.periodReported"
            :productStatus="status"
            @closeModal="showProductStatusModal = false"
            @newNoMatch="updateProductNoMatch"
            @associated="associationChosen"
            @stale="refreshFromBackend"
        />
        <!-- Matches Modal -->
        <!-- <MatchesModal 
            v-if="matchesModal"
            :parent_product="product"
            :selectedDate="props.selectedDate"
            @closeModal="closeMatchesModal"
            @newSuggestion="updateProductSuggestion"
            @noMatch="updateProductNoMatch"
        /> -->
        <!-- Delete Modal -->
        <DeleteProductModal 
            v-if="deleteModal"
            :parent_product="product"
            @closeModal="closeDeleteProductModal"
            @productDeleted="productDeleted"
        />
        <!-- Match Modal -->
        <MatchDetailsModal 
            v-if="showMatchModal"
            :parent_product="product"
            @closeModal="closeShowMatchModal"
        />
        <NoMatchModal 
            v-if="showNoMatchModal"
            :product="product"
            @closeModal="showNoMatchModal = false"
        />
        <UpdateQuantityModal 
            v-if="showUpdateQuantityModal"
            :product="product"
            @closeModal="closeUpdateQuantityModal"
            @quantityUpdated="updateProductQuantity"
        />
    </teleport>
</template>

<style scoped>
/* Chi ha chiesto meno movimento non deve subire una rotazione continua per
   sapere che si sta aspettando: restano il colore, l'etichetta e `aria-busy`.
   Sta qui e non nella configurazione di Tailwind perche' la variante
   `motion-reduce` non e' abilitata in questo progetto, e accenderla
   globalmente ricostruirebbe tutto il CSS per una riga. */
@media (prefers-reduced-motion: reduce)
{
    .animate-spin
    {
        animation: none;
    }
}
</style>
